'use client'

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Address } from 'viem'

const STORAGE_KEY = 'euler-subaccounts-v1'

export interface SubaccountState {
  // State per owner
  owner: Address | null
  activeSubaccountIds: number[] // Subaccounts with controllers (active positions)
  lastDiscoveryBlock: number
  isDiscovering: boolean

  // Actions
  setOwner: (owner: Address | null) => void
  setDiscoveryResult: (ids: number[], block: number) => void
  setDiscoveryFailed: (block: number) => void
  setIsDiscovering: (isDiscovering: boolean) => void
  addSubaccount: (id: number) => void
  removeSubaccount: (id: number) => void
  invalidate: () => void
  reset: () => void
}

interface PersistedSubaccountState {
  owner: Address | null
  activeSubaccountIds: number[]
  lastDiscoveryBlock: number
}

export const useSubaccountStore = create<SubaccountState>()(
  persist(
    (set, get) => ({
      owner: null,
      activeSubaccountIds: [],
      lastDiscoveryBlock: 0,
      isDiscovering: false,

      setOwner: (owner) => {
        const currentOwner = get().owner
        // If owner changed, reset the state
        if (currentOwner?.toLowerCase() !== owner?.toLowerCase()) {
          set({
            owner,
            activeSubaccountIds: [],
            lastDiscoveryBlock: 0,
            isDiscovering: false,
          })
        }
      },

      setDiscoveryResult: (ids, block) => {
        set({
          activeSubaccountIds: [...ids].sort((a, b) => a - b),
          lastDiscoveryBlock: block,
          isDiscovering: false,
        })
      },

      setDiscoveryFailed: (block) => {
        // Only update the block to prevent retry spam, keep existing activeSubaccountIds
        set({
          lastDiscoveryBlock: block,
          isDiscovering: false,
        })
      },

      setIsDiscovering: (isDiscovering) => set({ isDiscovering }),

      addSubaccount: (id) =>
        set((state) => {
          if (state.activeSubaccountIds.includes(id)) {
            return state
          }
          return {
            activeSubaccountIds: [...state.activeSubaccountIds, id].sort((a, b) => a - b),
          }
        }),

      removeSubaccount: (id) =>
        set((state) => ({
          activeSubaccountIds: state.activeSubaccountIds.filter((i) => i !== id),
        })),

      invalidate: () => {
        set({ lastDiscoveryBlock: 0 })
      },

      reset: () => {
        set({
          owner: null,
          activeSubaccountIds: [],
          lastDiscoveryBlock: 0,
          isDiscovering: false,
        })
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state): PersistedSubaccountState => ({
        owner: state.owner,
        activeSubaccountIds: state.activeSubaccountIds,
        lastDiscoveryBlock: state.lastDiscoveryBlock,
      }),
    },
  ),
)

/**
 * Get the first available subaccount ID (finds gaps or next sequential)
 */
export function getFirstAvailableSubaccountId(activeIds: number[]): number {
  const activeSet = new Set(activeIds)
  for (let i = 0; i < 256; i++) {
    if (!activeSet.has(i)) return i
  }
  return -1 // All 256 used (very unlikely)
}

/**
 * Check if a subaccount ID is active
 */
export function isSubaccountActive(activeIds: number[], id: number): boolean {
  return activeIds.includes(id)
}
